// SPDX-License-Identifier: GPL-3.0-only
// OpenSaga 7897237 source subset: game_obj.cpp KillPlayer(2694),
// KillGameObject(2366,2480-2548), ManageGameObjects(654-754);
// players.cpp ResetPlayerAI(1429), initial last-safe(1277),
// SetToLastSafePos(1604); move.cpp UpdateLastSafePosition(8004-8098).
// Ordinary on-foot player only. Player creation calls AddCreature(id,1)
// (players.cpp:120), setting motion_flags0x4002 (characters.cpp:860).
// Original vehicle/takeover, arcade,
// AI path checkpoints and level-specific respawn branches require host support.

function vec(value) {
  const p = Array.isArray(value) ? value : [value?.x, value?.y, value?.z];
  if (p.length !== 3 || !p.every(Number.isFinite)) throw new Error('Invalid respawn position.');
  return p.slice();
}
function integer(value, name, min = 0, max = 0xffffffff) {
  if (!Number.isInteger(value) || value < min || value > max) throw new Error(`Invalid ${name}.`);
  return value;
}

export function isOrdinarySafePosition(context, { safePlatformIds = [] } = {}) {
  if (!context || context.grounded !== true || context.alive !== true || context.onFoot !== true || context.playerControlled !== true) return false;
  if (context.doomed || context.disabled || context.skipSafeThisFrame || context.contextForbidsSafe || context.vehicleArea || context.takenOver || context.specialLevelRule) return false;
  const normal = vec(context.surfaceNormal);
  if (!(normal[1] > .9) || !Number.isFinite(context.floorHeight) || context.floorHeight === 2000000 || !Number.isFinite(context.lowerY) || !(context.lowerY > context.floorHeight - .1)) return false;
  // Null metadata is unknown, not a safe default. Source -1 means no surface
  // or layer; an actual known surface/layer requires its real flags.
  integer(context.surfaceIndex, 'surface index', -1, 127);
  integer(context.layer, 'terrain layer', -1, 127);
  if (context.surfaceIndex !== -1 && (!Number.isInteger(context.surfaceFlags) || (context.surfaceFlags & 0xc041))) return false;
  if (context.layer !== -1 && (!Number.isInteger(context.layerFlags) || (context.layerFlags & 1) || context.layer === 9)) return false;
  const allowed = id => id === -1 || safePlatformIds.includes(id);
  if (!Number.isInteger(context.platformId) || !Number.isInteger(context.supportingPlatformId)) return false;
  if (!(allowed(context.platformId) && allowed(context.supportingPlatformId)) && context.extraSafePlatform !== true) return false;
  return true;
}

export function createRespawnController({ initialPosition, maxHealth, motionFlags = 0x4002, modelFlags = null, persistentRespawn = null, initialFacing = 0, safePlatformIds = [] } = {}) {
  const initial = vec(initialPosition); integer(maxHealth, 'maximum health', 1, 127);
  if (!Number.isFinite(initialFacing) || !Array.isArray(safePlatformIds) || !safePlatformIds.every(Number.isInteger)) throw new Error('Invalid respawn context.');
  if (modelFlags !== null) integer(modelFlags, 'model flags');
  if (motionFlags !== null) integer(motionFlags, 'motion flags');
  if (persistentRespawn !== null && typeof persistentRespawn !== 'boolean') throw new Error('Invalid persistent respawn flag.');
  // The override is an explicit host-supplied source flag, not an assumption
  // that every game actor is persistent. Players.cpp derives it from bit27.
  const persistent = persistentRespawn ?? (modelFlags !== null ? !!(modelFlags & 0x08000000) : null);
  const respawnDelay = motionFlags === null || (motionFlags & 0x400) ? null : (motionFlags & 0x4000) ? 2 : persistent === true ? 1 : null;
  const state = { phase: 'alive', position: initial.slice(), lastSafePosition: initial.slice(), facing: initialFacing,
    movementFacing: initialFacing, velocity: [0, 0, 0], health: maxHealth, deathElapsed: 0, deathDuration: 0, respawnRemaining: 0, protectionRemaining: 0, deathCount: 0 };
  const events = [];
  const snapshot = () => ({ ...state, position: state.position.slice(), lastSafePosition: state.lastSafePosition.slice(), velocity: state.velocity.slice(),
    canTakeDamage: state.phase === 'alive' && state.protectionRemaining <= 0, canMove: state.phase === 'alive', visible: state.phase === 'alive', persistentRespawn: persistent, respawnDelay });
  const emit = (type, extra = {}) => events.push({ type, ...extra, state: snapshot() });
  return {
    snapshot,
    considerSafe(context) {
      if (state.phase !== 'alive' || !isOrdinarySafePosition(context, { safePlatformIds })) return false;
      const position = vec(context.position); state.lastSafePosition = position;
      if (Number.isFinite(context.movementFacing)) state.movementFacing = context.movementFacing;
      return true;
    },
    syncAlive({ position, movementFacing, velocity } = {}) {
      if (state.phase !== 'alive') return false;
      if (position !== undefined) state.position = vec(position);
      if (velocity !== undefined) state.velocity = vec(velocity);
      if (movementFacing !== undefined) { if (!Number.isFinite(movementFacing)) throw new Error('Invalid movement facing.'); state.movementFacing = movementFacing; }
      return true;
    },
    kill({ reason = 2, ignoreProtection = false, context = {} } = {}) {
      if (state.phase !== 'alive') return false;
      if (ignoreProtection) state.protectionRemaining = 0;
      if (state.protectionRemaining > 0) return false;
      if (![1, 2, 3].includes(reason)) throw new Error('Unsupported ordinary death reason.');
      if (respawnDelay === null || context.vehicle || context.takeover || context.arcade || context.nativeAi || context.specialLevelRule || context.actorDisabled) {
        emit('blocked', { reason: 'unresolved persistent flag or unsupported respawn context' }); return false;
      }
      const deathPosition = state.position.slice();
      state.health = 0; state.phase = 'death-delay'; state.deathElapsed = 0; state.deathDuration = reason === 2 || reason === 3 ? 0 : .5;
      state.respawnRemaining = 0; state.deathCount++;
      // KillGameObject relocates the player immediately; ordinary body parts
      // remain at deathPosition in the separate original debris system.
      state.position = state.lastSafePosition.slice(); state.velocity[0] = state.velocity[2] = 0;
      emit('killed', { reason, deathPosition, releaseInteractions: true }); return true;
    },
    update(dt) {
      if (!Number.isFinite(dt) || dt < 0 || dt > 1) throw new Error('Invalid respawn time step.');
      if (state.phase === 'alive') state.protectionRemaining = Math.max(0, Math.fround(state.protectionRemaining - dt));
      else if (state.phase === 'death-delay') {
        state.deathElapsed = Math.fround(state.deathElapsed + dt);
        if (state.deathElapsed >= state.deathDuration) {
          state.deathElapsed = state.deathDuration; state.phase = 'respawn-wait'; state.respawnRemaining = respawnDelay;
          emit('respawn-scheduled');
        }
      } else if (state.phase === 'respawn-wait') {
        state.respawnRemaining = Math.fround(state.respawnRemaining - dt);
        if (state.respawnRemaining <= 0) {
          state.respawnRemaining = 0; state.phase = 'alive'; state.position = state.lastSafePosition.slice();
          state.velocity = [0, -.1, 0]; state.facing = state.movementFacing; state.health = maxHealth; state.protectionRemaining = 2.5;
          emit('respawned', { resetPlayerMoves: true, resetCombat: true, resetJump: true });
        }
      }
      return events.splice(0);
    },
    drainEvents() { return events.splice(0); },
    reset({ position = initial, facing = initialFacing } = {}) {
      if (!Number.isFinite(facing)) throw new Error('Invalid reset facing.');
      state.phase = 'alive'; state.position = vec(position); state.lastSafePosition = state.position.slice(); state.facing = state.movementFacing = facing;
      state.velocity = [0, 0, 0]; state.health = maxHealth; state.deathElapsed = state.deathDuration = state.respawnRemaining = state.protectionRemaining = state.deathCount = 0; events.length = 0;
      return snapshot();
    },
    limitations: ['vehicle/takeover/arcade and native AI respawns', 'native level-specific respawn overrides', 'AI path checkpoint promotion', 'camera checkpoint restoration', 'death body-part debris', 'PC executable timing independently unverified'],
  };
}
